<template>
  <!-- Table Container Card -->
  <b-card no-body>

    <div class="m-2">

      <!-- Table Top -->
      <b-row>

        <!-- Per Page -->
        <b-col
          cols="12"
          md="6"
          class="d-flex align-items-center justify-content-start mb-1 mb-md-0"
        >
          <TableEntries
            v-model="perPage"
            :options="perPageOptions"
          />
          <Button
            v-if="getUserPermission('CREATE_TRANSACTION_RULE')"
            :label="$t('Add')"
            size="md"
            @click="readDisclaimerNote()"
          />
        </b-col>

        <!-- Search -->
        <b-col
          cols="12"
          md="6"
        >
          <div class="d-flex align-items-center justify-content-lg-end">
            <Button
              :label="$t('Filter')"
              size="md"
              icon="FilterIcon"
              show-icon
              class="mr-1"
              @click="showModal('modal-filter')"
            />
            <Button
              :label="$t('Reset Filter')"
              size="md"
              icon="RotateCwIcon"
              show-icon
              @click="resetFilter"
            />
          </div>
        </b-col>
      </b-row>

    </div>

    <b-table
      ref="blackListRef"
      :busy="isBusy"
      :per-page="pageSize"
      :current-page="currentPage"
      :items="transactionRuleData"
      responsive
      :fields="translatedTableHeaders"
      primary-key="id"
      show-empty
      :empty-text="$t('No records available')"
      :sort-by.sync="sortBy"
      :sort-desc.sync="sortDesc"
      class="position-relative"
    >
      <template #table-busy>
        <div class="text-center text-danger my-2">
          <b-spinner
            class="align-middle"
            variant="primary"
          />
        </div>
      </template>
      <template #cell(SN)="data">
        {{ getOverallIndex(data.index) }}
      </template>
      <template #cell(minimumAmount)="data">
        {{ toBalance(data.value) }}
      </template>
      <template #cell(status)="data">
        <b-badge
          v-if="data.value"
          pill
          :variant="getStatusColor(data.value)"
        >
          {{ data.value }}
        </b-badge>
      </template>

      <!-- Column: Actions -->
      <template #cell(actions)="data">
        <div class="text-nowrap">
          <!-- Dropdown -->
          <b-dropdown
            variant="link"
            toggle-class="p-0"
            no-caret
            :right="$store.state.appConfig.isRTL"
          >
            <template #button-content>
              <feather-icon
                icon="MoreVerticalIcon"
                size="16"
                class="align-middle text-body"
              />
            </template>
            <b-dropdown-item
              v-if="getUserPermission('MODIFY_TRANSACTION_RULE')"
              @click="onSelectTransactionRuleItem(data.item, $event.target, 'edit')"
            >
              <feather-icon icon="EditIcon" />
              <span class="align-middle ml-50"> {{ $t('Modify') }} </span>
            </b-dropdown-item>
            <b-dropdown-item @click="onSelectTransactionRuleItem(data.item, $event.target, 'view')">
              <feather-icon icon="EyeIcon" />
              <span class="align-middle ml-50"> {{ $t('View') }} </span>
            </b-dropdown-item>
            <b-dropdown-item
              v-if="getUserPermission(data.item.status === 'Active' ? 'DEACTIVATE_TRANSACTION_RULE' : 'ACTIVATE_TRANSACTION_RULE')"
              @click="editRuleStatus(data.item, $event.target)"
            >
              <feather-icon icon="CheckSquareIcon" />
              <span class="align-middle ml-50">{{
                data.item.status === 'Active' ? $t('Deactivate') : $t('Activate')
              }}</span>
            </b-dropdown-item>
          </b-dropdown>
        </div>
      </template>

    </b-table>

    <TablePagination
      v-model="currentPage"
      :from="from"
      :to="to"
      :total-rows="totalEntries"
      :per-page="perPage"
    />

    <!-- modal -->
    <b-modal
      id="modal-lg"
      ref="my-modal"
      :ok-title="buttonLabel"
      :cancel-title="$t('Cancel')"
      cancel-variant="outline-secondary"
      centered
      no-close-on-backdrop
      size="lg"
      :title="modalTitle"
      :ok-disabled="isLoading"
      @ok="handleSubmit"
      @hidden="resetModal"
    >
      <validation-observer ref="transactionRuleRules">
        <b-form>
          <b-row class="match-height">
            <b-col md="6">
              <SelectField
                id="rule"
                v-model="transactionRuleFormData.rule"
                :label="$t('Rule')"
                :placeholder="$t('Select')"
                :options="rulesOptions"
                :reduce="option => option.value"
                item="label"
                :error-name="$t('Rule')"
                rules="required"
                :readonly="isEditable"
                :set-selected="setTransactionRule"
              />
            </b-col>
            <b-col md="6">
              <TextAreaField
                id="description"
                v-model="transactionRuleFormData.description"
                :label="$t('Description')"
                :placeholder="$t('Enter Description')"
                :error-name="$t('Description')"
                rules="required"
                rows="2"
                :readonly="isReadonly"
              />
            </b-col>
            <b-col md="6">
              <InputNumberField
                id="value"
                v-model="transactionRuleFormData.value"
                :label="$t('Value')"
                :placeholder="$t('Enter Value')"
                type="text"
                :error-name="$t('Value')"
                rules="required"
                for="name"
                :readonly="isReadonly"
              />
            </b-col>
            <b-col md="6">
              <SelectField
                id="account-type"
                v-model="transactionRuleFormData.accountType"
                :label="$t('Account Type')"
                :options="customerTypeOptions"
                item="label"
                multiple
                :reduce="option => option.value"
                :placeholder="$t('Select')"
                :error-name="$t('Account Type')"
                rules="required"
                :readonly="isReadonly"
              />
            </b-col>
            <b-col md="6">
              <SelectField
                id="tier-level"
                v-model="transactionRuleFormData.tierLevel"
                :label="$t('Account Tier Level')"
                :options="accountTierOptions"
                item="label"
                :reduce="option => option.value"
                :placeholder="$t('Select')"
                :readonly="isReadonly"
              />
            </b-col>
            <b-col md="6">
              <SelectField
                id="action"
                v-model="transactionRuleFormData.action"
                :label="$t('Action')"
                :placeholder="$t('Select')"
                :options="translatedTransactionRuleAction"
                :reduce="option => option.value"
                item="label"
                :error-name="$t('Action')"
                rules="required"
                :readonly="isReadonly"
              />
            </b-col>
            <b-col md="6">
              <SelectField
                id="product-codes"
                v-model="transactionRuleFormData.productCodes"
                :label="$t('Product Code')"
                :options="productOptions"
                item="label"
                multiple
                :reduce="option => option.value"
                :placeholder="$t('Select')"
                :readonly="isReadonly"
              />
            </b-col>
            <b-col md="6">
              <InputNumberField
                id="minimum-amount"
                v-model="transactionRuleFormData.minimumAmount"
                :label="$t('Minimum Amount')"
                :placeholder="$t('Enter Minimum Amount')"
                type="text"
                for="name"
                :readonly="minimumAmountRequired === true ? minimumAmountRequired : isReadonly"
              />
            </b-col>

            <b-col md="6">
              <SelectField
                id="currency-code"
                v-model="transactionRuleFormData.currency"
                :label="$t('Currency')"
                :reduce="option => option"
                :options="currencyCodeOptions"
                item="label"
                :placeholder="$t('Select currency')"
                :error-name="$t('Currency')"
                rules="required"
                :readonly="isReadonly"
              />
            </b-col>
            <b-col md="6">
              <TextAreaField
                id="notification-email"
                v-model="transactionRuleFormData.notificationEmail"
                :label="$t('Notification Email')"
                :placeholder="$t('Enter Notification Email')"
                :error-name="$t('Notification Email')"
                rules="required"
                rows="2"
                :readonly="isReadonly"
              />
            </b-col>
          </b-row>
        </b-form>
      </validation-observer>
      <template
        v-if="isReadonly"
        #modal-footer="{ }"
      >
        <div />
      </template>
      <!-- <FullScreenSpinner :show="showSpinner" /> -->
    </b-modal>
    <!-- modal -->

    <!-- Disclaimer modal -->
    <b-modal
      id="disclaimer-modal"
      ref="my-disclaimer-modal"
      centered
      no-close-on-backdrop
      size="md"
      :title="$t('Disclaimer Information')"
      hide-footer
    >
      <validation-observer ref="disclaimerRules">
        <b-form>
          <b-row class="match-height">
            <b-col md="12">
              <p style="font-size: larger; font-weight: 700;">
                {{ $t('Disclaimer:') }}
              </p>

              <p style="text-align: justify; font-size: 16px; font-family:Arial, Helvetica, sans-serif; color: #222; ">
                {{ $t('This feature is intended to support the bank\'s efforts in monitoring potentially suspicious transactions. However, it is important to understand that this feature is a supplementary tool and should not be regarded as a complete or infallible solution for preventing fraudulent activities. The financial institution is expected to maintain and implement a comprehensive suite of security protocols and risk management strategies, of which this feature is a part.') }}
              </p>
              <p style="text-align: justify; font-size: 16px; font-family:Arial, Helvetica, sans-serif; color: #222; ">
                {{ $t('Although significant effort has been invested to ensure the effectiveness of this feature, the Vendor provides no warranty or guarantee that it will completely prevent fraud. Consequently, the Vendor shall not be liable for any losses, damages, or unauthorized transactions that may occur, notwithstanding the implementation of this feature. The responsibility for establishing, managing, and continuously improving fraud prevention protocols to enhance security and mitigate risk rests with the financial institution.') }}
              </p>

            </b-col>
            <b-col md="12">
              <ValidationProvider
                :name="$t('Terms and Conditions')"
                rules="required"
              >
                <b-form-group
                  class="pt-2"
                  label=""
                  :state="!customErrors.agreeToDisclaimer"
                  :invalid-feedback="customErrors.agreeToDisclaimer"
                >
                  <b-form-checkbox
                    v-model="ruleFormData.hasAcceptedDisclaimer"
                    name="check-button"
                    inline
                    :disabled="isReadonly"
                    @input="setDisclaimerCheck"
                  >
                    {{ $t('I have Read, Understood and Accepted the Disclaimer.') }}
                  </b-form-checkbox>
                  <b-form-invalid-feedback>
                    {{ customErrors.agreeToDisclaimer }}
                  </b-form-invalid-feedback>
                </b-form-group>
              </ValidationProvider>
            </b-col>

            <b-col
              class="d-flex flex-sm-row flex-column flex-wrap justify-content-end mt-3"
              md="12"
            >
              <Button
                :label="$t('No, Thanks')"
                size="md"
                variant="outline-secondary"
                class="mr-1"
                @click="cancelRuleAction"
              />
              <Button
                :label="$t('Yes, I want to Proceed')"
                size="md"
                @click="performRuleAction"
              />
            </b-col>
          </b-row>
        </b-form>
      </validation-observer>
      <template
        v-if="isReadonly"
        #modal-footer="{ }"
      >
        <div />
      </template>
    </b-modal>
    <!-- Disclaimer modal -->

    <!-- Filter modal -->
    <b-modal
      id="modal-filter"
      ref="my-modal"
      :ok-title="$t('Apply')"
      :cancel-title="$t('Cancel')"
      cancel-variant="outline-secondary"
      no-close-on-backdrop
      centered
      size="lg"
      :title="$t('Filters')"
      modal-footer
      @ok="fetchTransactionRuleList"
      @cancel="resetFilters"
    >
      <b-row class="match-height">
        <!-- Name -->
        <b-col md="6">
          <InputField
            id="name"
            v-model="filter.name"
            :label="$t('Name')"
            :placeholder="$t('Enter name')"
            type="text"
          />
        </b-col>
        <!-- Name -->
        <b-col md="6">
          <SelectField
            id="currency-code"
            v-model="filter.currency"
            :label="$t('Currency')"
            :reduce="option => option"
            :options="currencyCodeOptions"
            item="label"
            :placeholder="$t('Select currency')"
          />
        </b-col>
        <b-col md="6">
          <SelectField
            id="action"
            v-model="filter.action"
            :label="$t('Action')"
            :placeholder="$t('Enter Action')"
            :options="translatedTransactionRuleAction"
            :reduce="option => option.value"
            item="label"
          />
        </b-col>
        <b-col md="6">
          <SelectField
            id="transaction-direction"
            v-model="filter.transactionDirection"
            :label="$t('Transaction Direction')"
            :placeholder="$t('Enter Transaction Direction')"
            :options="translatedTransactionDirectionOptions"
            :reduce="option => option.value"
            item="label"
          />
        </b-col>
        <b-col md="6">
          <SelectField
            id="status"
            v-model="filter.status"
            :label="$t('Status')"
            :placeholder="$t('Enter Status')"
            :options="translatedStatusOptions"
            :reduce="option => option.value"
            item="label"
          />
        </b-col>

      </b-row>
    </b-modal>
    <!-- Filter modal -->
    <FullScreenSpinner :show="showSpinner" />
  </b-card>

</template>

<script>
import {
  BCard,
  BRow,
  BCol,
  BTable,
  BSpinner,
  BModal,
  BForm,
  BDropdownItem,
  BDropdown,
  BBadge,
  BFormCheckbox,
  BFormGroup,
  BFormInvalidFeedback,
} from 'bootstrap-vue'
import { ValidationObserver, ValidationProvider } from 'vee-validate'
import { required } from '@validations'
import { modalMixins } from '@core/mixins/modal'
import { tableMixins } from '@core/mixins/table'
import { sweetAlert } from '@core/mixins/sweetAlert'
import { mapActions } from 'vuex'
import { permissionMixin } from '@core/mixins/permission'
import getStatusColor from '@/helpers/getStatusColor'
import { toBalance, toKobo } from '@/helpers/formatAmount'
import FullScreenSpinner from '@/views/components/spinner/FullScreenSpinner.vue'
import { fraudMonitorPermissions } from '@/helpers/permissions'
import { accountCustomerTypes, accountTier } from '@/helpers/data'
import TablePagination from '@/views/components/table/TablePagination.vue'
import TableEntries from '@/views/components/table/TableEntries.vue'
import InputField from '../../forms/InputField.vue'
import SelectField from '../../forms/SelectField.vue'
import InputNumberField from '../../forms/InputNumberField.vue'
import TextAreaField from '../../forms/TextAreaField.vue'
import Button from '../../components/button/Button.vue'

const TransactionDirectionOptions = [
  { value: 'Inflow', label: 'Inflow' },
  { value: 'Outflow', label: 'Outflow' },
]

const StatusOptions = [
  { value: 'Active', label: 'Active' },
  { value: 'InActive', label: 'InActive' },
]

const TransactionRuleAction = [
  { value: 'DoNothing', label: 'Do Nothing' },
  { value: 'PND', label: 'PND' },
  { value: 'PNC', label: 'PNC' },
  { value: 'Freeze', label: 'Freeze' },
  { value: 'Lien', label: 'Lien' },
]

export default {
  components: {
    BCard,
    BRow,
    BCol,
    SelectField,
    BTable,
    BModal,
    BForm,
    BSpinner,
    ValidationObserver,
    ValidationProvider,
    InputField,
    BDropdownItem,
    BDropdown,
    BBadge,
    TablePagination,
    Button,
    TextAreaField,
    InputNumberField,
    TableEntries,
    FullScreenSpinner,
    BFormCheckbox,
    BFormGroup,
    BFormInvalidFeedback,
  },
  mixins: [modalMixins, tableMixins, sweetAlert, permissionMixin],
  data() {
    return {
      query: '',
      toBalance,
      getStatusColor,
      isReadonly: false,
      isEditable: false,
      showSpinner: false,
      sortBy: 'name',
      buttonLabel: this.$t('Save'),
      required,
      modalTitle: this.$t('Create Transaction Rule'),
      sortDesc: false,
      tableHeaders: [
        { key: 'SN', label: 'S/N' },
        { key: 'name', sortable: true, label: 'Name' },
        { key: 'description', sortable: true, label: 'Description' },
        { key: 'code', sortable: true, label: 'Code' },
        { key: 'valueString', sortable: true, label: 'Value' },
        { key: 'minimumAmount', sortable: true, label: 'Minimum Amount' },
        { key: 'accountType', sortable: true, label: 'Account Type' },
        { key: 'action', sortable: true, label: 'Action' },
        { key: 'currency', sortable: true, label: 'Currency' },
        { key: 'transactionDirection', sortable: true, label: 'Transaction Direction' },
        { key: 'notificationEmail', sortable: true, label: 'Notification Email' },
        { key: 'status', sortable: true, label: 'Status' },
        { key: 'actions', label: 'Actions' },
      ],
      transactionRuleFormData: {
        accountType: null,
        productCodes: null,
        tierLevel: null,
        rule: null,
        description: '',
        value: null,
        minimumAmount: null,
        action: null,
        currency: '',
        notificationEmail: '',
        id: '',
      },
      filter: {
        accountNumber: '',
        bankCode: '',
      },
      transactionRuleData: [],
      // TransactionRuleAction: [
      //   { value: 'DoNothing', label: 'Do Nothing' },
      //   { value: 'PND', label: 'PND' },
      //   { value: 'PNC', label: 'PNC' },
      //   { value: 'Freeze', label: 'Freeze' },
      //   { value: 'Lien', label: 'Lien' },
      // ],
      // customerTypeOptions: accountCustomerTypes,
      currencyCodeOptions: [],
      // TransactionDirectionOptions: [
      //   { value: 'Inflow', label: 'Inflow' },
      //   { value: 'Outflow', label: 'Outflow' },
      // ],
      // StatusOptions: [
      //   { value: 'Active', label: 'Active' },
      //   { value: 'InActive', label: 'InActive' },
      // ],
      // accountTierOptions: accountTier,
      productOptions: [],
      rulesOptions: [],
      minimumAmountRequired: null,
      customErrors: {
        agreeToDisclaimer: '',
      },
      ruleFormData: {
        hasAcceptedDisclaimer: false,
      },
    }
  },
  computed: {
    translatedTableHeaders() {
      return this.tableHeaders.map(header => ({ ...header, label: header.label ? this.$t(header.label) : header.key }))
    },
    customerTypeOptions() {
      return accountCustomerTypes.map(header => ({ ...header, label: header.label ? this.$t(header.label) : header.value }))
    },
    accountTierOptions() {
      return accountTier.map(header => ({ ...header, label: header.label ? this.$t(header.label) : header.value }))
    },
    translatedTransactionDirectionOptions() {
      return TransactionDirectionOptions.map(header => ({ ...header, label: header.label ? this.$t(header.label) : header.value }))
    },
    translatedStatusOptions() {
      return StatusOptions.map(header => ({ ...header, label: header.label ? this.$t(header.label) : header.value }))
    },
    translatedTransactionRuleAction() {
      return TransactionRuleAction.map(header => ({ ...header, label: header.label ? this.$t(header.label) : header.value }))
    },
  },
  watch: {
    currentPage(newValue) {
      this.currentPage = newValue
      this.fetchTransactionRuleList()
    },
    perPage(newValue) {
      this.perPage = newValue
      this.fetchTransactionRuleList()
    },
  },
  mounted() {
    this.fetchTransactionRuleList()
    this.fetchAllCurrencies()
    this.fetchCASAProducts()
    this.fetchTransactionRules()
  },
  methods: {
    ...mapActions({
      getTransactionRule: 'fraudmonitor/getTransactionRule',
      updateRuleStatus: 'fraudmonitor/updateTransactionStatus',
      getAllCurrencies: 'account/getAllCurrenciesAction',
      createTransactionRule: 'fraudmonitor/createTransactionRuleAction',
      updateTransactionRule: 'fraudmonitor/updateTransactionRuleAction',
      getTransactionRules: 'fraudmonitor/getTransactionRules',
      getCASAProduct: 'account/getProducts',

    }),
    readDisclaimerNote() {
      this.$root.$emit('bv::show::modal', 'disclaimer-modal')
    },
    performRuleAction() {
      const valid = this.ruleFormData.hasAcceptedDisclaimer
      if (valid) {
        this.hideModal('disclaimer-modal')
        this.$root.$emit('bv::show::modal', 'modal-lg')
        this.ruleFormData.hasAcceptedDisclaimer = false
      } else {
        this.customErrors.agreeToDisclaimer = this.$t('You must agree to the terms of this Disclaimer to proceed')
      }
    },
    cancelRuleAction() {
      this.hideModal('disclaimer-modal')
      this.ruleFormData.hasAcceptedDisclaimer = false
    },
    setDisclaimerCheck(checkValue) {
      if (!checkValue) {
        this.isSubmitDisabled = true
        this.customErrors.agreeToDisclaimer = this.$t('You must agree to the terms of this Disclaimer to proceed')
      } else {
        this.isSubmitDisabled = false
        this.customErrors.agreeToDisclaimer = ''
      }
    },
    setTransactionRule(rule) {
      const isMinimumAmountRequired = ['1', '2'].includes(rule)
      this.minimumAmountRequired = isMinimumAmountRequired

      if (isMinimumAmountRequired) {
        this.transactionRuleFormData.minimumAmount = null
      }
    },
    async fetchTransactionRuleList() {
      try {
        this.isBusy = true
        this.hideModal('modal-filter')
        const response = await this.getTransactionRule({
          search: this.filter,
          page: this.currentPage,
          limit: this.perPage,
          permission: fraudMonitorPermissions.VIEW_TRANSACTION_RULE,
        })
        const { status, data } = response
        if (status === 200 && data.data) {
          this.transactionRuleData = data.data.map(item => ({
            ...item,
            // Add space in between
            name: item.name ? item.name.replace(/([a-z])([A-Z])/g, '$1 $2') : '',
          }))
          this.totalCount = data.recordCount
        } else {
          this.transactionRuleData = []
          this.totalCount = 0
        }
      } catch (error) {
        this.alertError({ text: error })
      } finally {
        this.isBusy = false
      }
    },
    async fetchCASAProducts() {
      try {
        const response = await this.getCASAProduct({
          search: { type: 'Savings' },
          page: 0,
          limit: 0,
          permission: fraudMonitorPermissions.VIEW_TRANSACTION_RULE,
        })
        const { status, data } = response
        if (status === 200 && data.data) {
          this.productOptions = data.data.filter(dataItem => dataItem.status === 'Active').map(item => ({ label: item.name, value: item.code.toString() }))
        }
      } catch (error) {
        this.alertError({ text: error })
      } finally {
        this.isBusy = false
      }
    },
    async handleSubmit(bvModalEvt) {
      try {
        bvModalEvt.preventDefault()
        this.$refs.transactionRuleRules.validate().then(async success => {
          if (success) {
            const text1 = this.$t('Are you sure you want to')
            const text2 = this.$t('Transaction Rule?')
            this.confirm(this.transactionRuleActions, {
              text: `${text1} ${this.buttonLabel} ${text2}`,
            })
          }
        })
      } catch (error) {
        this.hideModal('modal-lg')
        this.alertError({ text: error })
      } finally {
        this.hideModal('modal-lg')
      }
    },
    async transactionRuleActions() {
      try {
        this.isLoading = true
        this.showSpinner = true
        const formData = {
          ...this.transactionRuleFormData,
          accountType: this.transactionRuleFormData.accountType ? this.transactionRuleFormData.accountType.toString() : null,
          productCodes: this.transactionRuleFormData.productCodes ? this.transactionRuleFormData.productCodes.toString() : null,
          minimumAmount: this.transactionRuleFormData.minimumAmount ? toKobo(this.transactionRuleFormData.minimumAmount) : null,
          rule: this.transactionRuleFormData.rule ? Number(this.transactionRuleFormData.rule) : null,
        }
        const response = this.buttonLabel === 'Save'
          ? await this.createTransactionRule({ formData, permission: fraudMonitorPermissions.CREATE_TRANSACTION_RULE })
          : await this.updateTransactionRule({ formData, permission: fraudMonitorPermissions.MODIFY_TRANSACTION_RULE })
        const { status } = await response
        if (status === 200) {
          this.fetchTransactionRuleList()
          this.hideModal('modal-lg')
          const yourRequest = this.$t('Your request to')
          const wasSuccessful = this.$t('Transaction Rule was successful')
          this.alertSuccess({
            text: `${yourRequest} ${this.buttonLabel} ${wasSuccessful}`,
          })
          this.showSpinner = false
        }
      } catch (error) {
        this.hideModal('modal-lg')
        const yourRequest = this.$t('Your request to')
        const reasonText = this.$t('Transaction Rule failed. Reason:')
        error.response.data.errors.forEach(responseError => {
          this.alertError({ text: `${yourRequest} ${this.buttonLabel} ${reasonText} ${responseError.errorMessage}` })
        })
      } finally {
        this.hideModal('modal-lg')
        this.showSpinner = false
      }
    },
    async onSelectTransactionRuleItem(items, activity, val) {
      this.isEditable = true
      this.transactionRuleFormData.currency = items.currency
      this.transactionRuleFormData.value = items.value
      this.transactionRuleFormData.minimumAmount = items.minimumAmount ? toBalance(items.minimumAmount) : null
      this.transactionRuleFormData.action = items.action
      this.transactionRuleFormData.notificationEmail = items.notificationEmail
      this.transactionRuleFormData.accountType = items.accountType ? items.accountType.split(',') : null
      this.transactionRuleFormData.tierLevel = items.tierLevel
      this.transactionRuleFormData.productCodes = items.productCodes ? items.productCodes.split(',') : null
      this.transactionRuleFormData.rule = items.code
      this.transactionRuleFormData.description = items.description
      this.transactionRuleFormData.id = items.id
      this.isEditable = true
      this.minimumAmountRequired = !!(items.code === '1' || items.code === '2')
      this.isReadonly = val === 'edit' ? this.isReadonly = false : this.isReadonly = true
      this.modalTitle = val === 'edit' ? this.$t('Edit Transaction Rule') : this.$t('View Transaction Rule')
      this.buttonLabel = this.$t('Update')
      this.$root.$emit('bv::show::modal', 'modal-lg')
    },
    async editRuleStatus(items) {
      const { id, name } = items
      const actionType = items.status === 'Active' ? 'Inactive' : 'Active'
      const action = items.status === 'Active' ? this.$t('Deactivate') : this.$t('Activate')
      const permission = items.status === 'Active' ? 'DEACTIVATE_TRANSACTION_RULE' : 'ACTIVATE_TRANSACTION_RULE'

      try {
        const updateStatus = async () => {
          this.showSpinner = true
          const statusValues = {
            id,
            name,
          }
          const response = await this.updateRuleStatus({ statusValues, actionType, permission })
          if (response.status === 200) {
            this.fetchTransactionRuleList()
            const requestText = this.$t('Your request to')
            const successText = this.$t('Transaction Rule was successful')
            this.alertSuccess({ text: `${requestText} ${action} ${successText}` })
            this.showSpinner = false
          }
        }
        const areYouSure = this.$t('Are you sure you want to')
        const thisText = this.$t('this Transaction Rule?')
        this.confirm(updateStatus, { text: `${areYouSure} ${action} ${thisText}` })
      } catch (error) {
        const yourRequest = this.$t('Your request to')
        const reasonText = this.$t('Transaction Rule failed. Reason:')
        this.alertError({ text: `${yourRequest} ${action} ${reasonText} ${error}` })
      } finally {
        this.showSpinner = false
      }
    },
    async fetchAllCurrencies() {
      try {
        const response = await this.getAllCurrencies({ permission: fraudMonitorPermissions.VIEW_TRANSACTION_RULE })
        const { status, data } = await response
        if (status === 200 && data.data) {
          this.currencyCodeOptions = data.data.filter(item => item.status === 'Active').reduce((acc, curr) => [...acc, curr.code], [])
        }
      } catch (error) {
        this.handleError(error)
      }
    },
    async fetchTransactionRules() {
      try {
        const response = await this.getTransactionRules({ permission: fraudMonitorPermissions.VIEW_TRANSACTION_RULE })
        const { status, data } = await response
        if (status === 200 && data.data) {
          this.rulesOptions = data.data.map(item => ({ label: item.description, value: item.index.toString() }))
        }
      } catch (error) {
        this.handleError(error)
      }
    },
    resetModal() {
      this.transactionRuleFormData = { ...null }
      this.isEditable = false
      this.isLoading = false
      this.isReadonly = false
      this.minimumAmountRequired = false
      this.modalTitle = this.$t('Create Transaction Rule')
      this.buttonLabel = this.$t('Save')
    },
    resetFilters() {
      this.filter = { ...null }
    },
    resetFilter() {
      this.filter = { ...null }
      this.fetchTransactionRuleList()
    },
  },
}
</script>
